import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import {
  isPanoramaQueryKey,
  PERSISTED_QUERY_PARAMS_STORAGE_KEY
} from '@/config/persisted-query-params/constants';
import { APP_QUERY_WRITE_STATE } from '@/config/persisted-query-params/navigationSource';
import { readBag } from '@/config/persisted-query-params/readBag';
import { resolveStorageKey } from '@/config/persisted-query-params/resolveStorageKey';
import { useApp } from '@/contexts/app-context';

import { PersistedQueryParamsContext } from './context';
import usePersistedQueryParams from './usePersistedQueryParams';

type Props = { children: ReactNode };

export const PersistedQueryParamsProvider = ({ children }: Props) => {
  const [, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { selectedRegion, selectedCropSeason } = useApp();

  usePersistedQueryParams();

  const clearForCurrentRoute = useCallback(() => {
    const storageKey = resolveStorageKey(pathname, {
      region: selectedRegion,
      cropSeason: selectedCropSeason
    });
    const bag = readBag();
    delete bag[storageKey];
    localStorage.setItem(
      PERSISTED_QUERY_PARAMS_STORAGE_KEY,
      JSON.stringify(bag)
    );
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        [...next.keys()]
          .filter(isPanoramaQueryKey)
          .forEach(key => next.delete(key));
        return next;
      },
      { replace: true, state: APP_QUERY_WRITE_STATE }
    );
  }, [pathname, selectedRegion, selectedCropSeason, setSearchParams]);

  const clearForRoute = useCallback(
    (path: string) => {
      const storageKey = resolveStorageKey(path, {
        region: selectedRegion,
        cropSeason: selectedCropSeason
      });
      const bag = readBag();
      delete bag[storageKey];
      localStorage.setItem(
        PERSISTED_QUERY_PARAMS_STORAGE_KEY,
        JSON.stringify(bag)
      );
    },
    [selectedRegion, selectedCropSeason]
  );

  const clearAll = useCallback(() => {
    localStorage.removeItem(PERSISTED_QUERY_PARAMS_STORAGE_KEY);
  }, []);

  const value = useMemo(
    () => ({ clearForCurrentRoute, clearForRoute, clearAll }),
    [clearForCurrentRoute, clearForRoute, clearAll]
  );

  return (
    <PersistedQueryParamsContext.Provider value={value}>
      {children}
    </PersistedQueryParamsContext.Provider>
  );
};
